import { useCurrentSite } from '@hotjar/site';
import type { TrackFunctions } from '@hotjar/webapp/hooks/useTrack';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { stripDomain } from '@hotjar/webapp/utils/urls';
import { useCallback, useMemo } from 'react';
import { RecordingIconLink } from './RecordingIconLink';
import { RecordingButtonLink } from './RecordingButtonLink';

const appendFeedbackResponseOpener = (targetUrl: string, opener: string) => {
  const url = new URL(targetUrl);
  url.searchParams.append('opener', opener);

  return url.href;
};

export interface RecordingLinkProps {
  opener: string;
  type: 'button' | 'icon';
  trackTriggerView: string;
  trackUiPosition: string;
  coveragePercentage: number;
  url: string | null | undefined;
  outlinedIcon?: boolean;
}

const useTrackingEvent = (
  trackFn: TrackFunctions['click'] | TrackFunctions['viewElement'],
  eventName: string,
  trackingData: {
    trackTriggerView: string;
    trackUiPosition: string;
    coveragePercentage: number;
  },
) => {
  const { data: currentSite } = useCurrentSite();
  return useCallback(
    () =>
      currentSite &&
      trackFn(eventName, trackingData.trackUiPosition, {
        /* eslint-disable camelcase */
        state_event_was_triggered_on: trackingData.trackTriggerView,
        url_event_was_triggered_on: stripDomain(window.location.href),
        account_id: currentSite.account.id,
        organization_id: currentSite.organization.id,
        site_id: currentSite.id,
        shown_traffic_coverage: trackingData.coveragePercentage,
        /* eslint-enable camelcase */
      }),
    [
      currentSite,
      eventName,
      trackFn,
      trackingData.coveragePercentage,
      trackingData.trackTriggerView,
      trackingData.trackUiPosition,
    ],
  );
};

export function RecordingLink({
  opener,
  type,
  trackTriggerView,
  trackUiPosition,
  coveragePercentage,
  url,
  outlinedIcon,
}: RecordingLinkProps): JSX.Element {
  const track = useTrack();

  const trackingData = {
    trackTriggerView,
    trackUiPosition,
    coveragePercentage,
  };

  const trackHover = useTrackingEvent(
    track.viewElement,
    'play_recording_disabled-on_hover_tooltip',
    trackingData,
  );
  const trackClick = useTrackingEvent(track.click, 'play_recording', trackingData);

  const href = useMemo(
    () =>
      url === null || url === undefined || url.length === 0
        ? undefined
        : appendFeedbackResponseOpener(url, opener),
    [url, opener],
  );

  if (type === 'icon') {
    return (
      <RecordingIconLink
        href={href}
        trackHover={trackHover}
        trackClick={trackClick}
        outlinedIcon={outlinedIcon}
      />
    );
  }

  return <RecordingButtonLink href={href} trackHover={trackHover} trackClick={trackClick} />;
}
